import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Collapse,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Visibility as VisibilityIcon,
  Timeline as TimelineIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const UserGuide: React.FC = () => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSection, setExpandedSection] = useState<string | null>('Getting Started');

  const handleSectionToggle = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = {
    gettingStarted: [
      {
        title: 'Welcome to AmslerGrid Daily',
        content: 'Your daily companion for monitoring vision changes with the Amsler grid test.',
        icon: <VisibilityIcon />,
      },
      {
        title: 'Taking Your First Test',
        content: 'Learn how to perform the Amsler grid test correctly for accurate results.',
        icon: <TimelineIcon />,
      },
      {
        title: 'Understanding Results',
        content: 'Interpret your test results and track changes over time.',
        icon: <HelpIcon />,
      }
    ],
    features: [
      {
        title: 'Daily Reminders',
        content: 'Set up personalized reminders with daily, weekly, or monthly options. Customize the time and receive notifications across all your devices.',
        icon: <TimelineIcon />,
      },
      {
        title: 'Progress Tracking',
        content: 'View your last 10 test results and track vision changes.',
        icon: <TimelineIcon />,
      },
      {
        title: 'Data Export',
        content: 'Export your test results to share with healthcare providers.',
        icon: <TimelineIcon />,
      }
    ],
    troubleshooting: [
      {
        title: 'Common Issues',
        content: 'Learn about common problems and their solutions when taking the Amsler Grid test.',
        icon: <HelpIcon />,
      },
      {
        title: 'Technical Support',
        content: 'Get help with technical issues related to the app.',
        icon: <HelpIcon />,
      },
      {
        title: 'Contact Support',
        content: 'Reach out to our support team for assistance.',
        icon: <HelpIcon />,
      }
    ]
  };

  const renderSection = (title: string, items: { title: string; content: string; icon: React.ReactNode }[]) => (
    <Box sx={{ mb: 4 }}>
      <Box
        onClick={() => handleSectionToggle(title)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          py: 2,
          px: 3,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: 1,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          }
        }}
      >
        <Typography variant="h6" sx={{ flex: 1 }}>{title}</Typography>
        <IconButton 
          sx={{ color: 'inherit' }}
          size="small"
        >
          {expandedSection === title ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
      <Collapse in={expandedSection === title}>
        <List sx={{ mt: 2 }}>
          {items.map((item, index) => (
            <React.Fragment key={index}>
              <ListItem sx={{ 
                backgroundColor: theme.palette.background.default,
                borderRadius: 1,
                mb: 2,
                boxShadow: theme.shadows[1]
              }}>
                <ListItemIcon sx={{ color: theme.palette.primary.main }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      {item.title}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {item.content}
                    </Typography>
                  }
                />
              </ListItem>
              {index < items.length - 1 && <Divider sx={{ my: 2 }} />}
            </React.Fragment>
          ))}
        </List>
      </Collapse>
    </Box>
  );

  return (
    <Box>
      <Typography 
        variant="h5" 
        component="h1" 
        gutterBottom
        color="primary"
        sx={{ 
          fontWeight: 'bold',
          mb: 3
        }}
      >
        User Guide
      </Typography>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search the guide..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            sx: {
              backgroundColor: theme.palette.background.paper,
              '&:hover': {
                backgroundColor: theme.palette.background.default,
              }
            }
          }}
          sx={{ 
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
      </Box>

      {renderSection('Getting Started', sections.gettingStarted)}
      {renderSection('Features', sections.features)}
      {renderSection('Troubleshooting', sections.troubleshooting)}

      <Box 
        sx={{ 
          mt: 3,
          p: 2,
          backgroundColor: theme.palette.info.light + '20',
          borderRadius: 1,
          border: `1px solid ${theme.palette.info.main}`,
        }}
      >
        <Typography variant="body1" paragraph>
          Need more help? Contact our support team at{' '}
          <Link 
            href="mailto:support@kubomaster.llc" 
            color="primary"
          >
            support@kubomaster.llc
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default UserGuide; 